/**
 * Runtime model for the clinic filter "condition blueprints" returned by
 * `GET /clinics/available-conditions`. The frontend builds its filter UI from
 * these blueprints instead of hard-wiring every condition.
 *
 * Forward-compatibility contract (per the API): always IGNORE fragment `kind`s
 * you do not recognize — never fail hard on them.
 */

export type ClinicConditionType =
  | 'LIST'
  | 'RANGE'
  | 'FLAG'
  | 'OPERATOR'
  | 'EQUALS'
  | 'SUBSTRING'
  | 'OTHER';

/**
 * The grains an operating number can be filtered at.
 *
 * `CLINIC`, `DEPARTMENT` and `DEQS` are structural — they say which composite
 * a condition may sit in. `ANNUAL_ACCOUNT` (BDA-5849, KB-15) is different in
 * kind: it marks a Jahresabschluss metric, which is selectable at the TOP
 * level alongside `CLINIC` and has no wrapper condition of its own.
 */
export const CLINIC_CONDITION_TARGETS = [
  'CLINIC',
  'DEPARTMENT',
  'DEQS',
  'ANNUAL_ACCOUNT',
] as const;

export type ClinicConditionTarget = (typeof CLINIC_CONDITION_TARGETS)[number];

/**
 * The grains offered at the top level of the filter — a Kennzahl condition
 * outside any composite may name a clinic metric or a Jahresabschluss metric.
 */
export const CLINIC_TOP_LEVEL_TARGETS: readonly ClinicConditionTarget[] = [
  'CLINIC',
  'ANNUAL_ACCOUNT',
];

/** Keyed `additionalInformation` fragment; unknown `kind`s carry opaque extras. */
export interface ClinicConditionFragment {
  kind: string;
  [key: string]: unknown;
}

/** One entry of the `available-conditions` response. */
export interface ClinicConditionBlueprint {
  id: string;
  type: ClinicConditionType;
  displayName: Record<string, string>;
  additionalInformation: ClinicConditionFragment[];
}

/**
 * The taxonomy a catalog key belongs to (BDA-6251) — the Versorgungsschwerpunkt
 * for the `V*` Leistungsangebot families, the Fachgebiet for AQ/ZF and PQ/ZP.
 * Every key of one `catalog-keys/{family}` call shares the same one.
 *
 * Same SHAPE as {@link ClinicOperatingNumberCategory} and deliberately not the
 * same type: that one is the Kennzahl catalog's gliederung from a different
 * endpoint, and the two taxonomies are free to diverge.
 */
export interface ClinicCatalogCategory {
  id: string;
  label: Record<string, string>;
  order: number;
}

/** A selectable option for a catalog-backed LIST condition (`catalog-keys/{family}`). */
export interface ClinicCatalogOption {
  key: string;
  label: string;
  /** Optional per the API — `enum` sources and older families carry none. */
  category?: ClinicCatalogCategory;
}

/** A category heading plus its options, for a grouped LIST picker. */
export interface ClinicCatalogOptionGroup {
  id: string;
  label: string;
  items: ClinicCatalogOption[];
}

/**
 * The UI taxonomy an operating number belongs to — the same grouping the QB
 * export's field list uses, so a picker and an export show one set of
 * sections. Optional per the API.
 */
export interface ClinicOperatingNumberCategory {
  id: string;
  label: Record<string, string>;
  order: number;
}

/**
 * A heading INSIDE a category (BDA-6247) — same shape as the category itself.
 *
 * `order` is scoped to the category, not global: `PFLEGEFACHKRAEFTE` and
 * `HYGIENEPERSONAL` are both 10, in different categories. So a section is only
 * ever sorted among its own siblings, never across the catalog.
 */
export type ClinicOperatingNumberSection = ClinicOperatingNumberCategory;

/**
 * The frontend SURFACES an operating number may be offered on (BDA-6070) —
 * `supportedUsages`, exactly parallel to `supportedGrains` and serialized by
 * backend enum NAME.
 *
 * Only surfaces with a real backend accept/reject exist here: `FILTER` is the
 * `operatingNumber` (Kennzahl) filter condition, `CALCULATION` a
 * Kennzahlen-Benchmarking term leaf. The two are not the same set — a
 * calculation leaf compiles only against a clinic-year or annual-account
 * column, so the DEQS metrics carry `FILTER` alone and used to be offered in
 * the term picker as a dead end that answered `INVALID_TERM`. Display is
 * deliberately NOT a usage: whatever an endpoint returns is already
 * displayable, so a name resolver must never filter on this.
 */
export const CLINIC_OPERATING_NUMBER_USAGES = [
  'FILTER',
  'CALCULATION',
] as const;

export type ClinicOperatingNumberUsage =
  (typeof CLINIC_OPERATING_NUMBER_USAGES)[number];

/** Raw operating-number descriptor from `GET /clinics/available-operating-numbers`. */
export interface ClinicOperatingNumberDescriptor {
  id: string;
  label: Record<string, string>;
  unit?: string;
  supportedGrains: ClinicConditionTarget[];
  /**
   * Which pickers may offer this metric — read through
   * {@link clinicOperatingNumberSupportsUsage}, never directly.
   *
   * Optional because the field is purely additive (BDA-6070): a backend that
   * predates it sends nothing at all.
   */
  supportedUsages?: ClinicOperatingNumberUsage[];
  category?: ClinicOperatingNumberCategory;
  /**
   * Where this metric sits INSIDE its category (BDA-6247) — the second level
   * of the Kennzahl tree. Null for a category that has no sections at all;
   * a category either sections all of its metrics or none of them.
   */
  section?: ClinicOperatingNumberSection | null;
  /**
   * The metric this one rolls up into (BDA-6247), e.g. `aai` → `aa`, or
   * `fachaerzte` → `aerzteOhneBelegaerzte` (the CHILD names the parent — the
   * ticket's prose has that arrow the other way round, the wire is right).
   *
   * Null for a top-level position, and also where the real parent is not a
   * benchmarkable metric: `g18e` sits under `g18` in the statement, but `g18`
   * is not in the catalog, so the backend reports no parent rather than a
   * dangling one. Every `parentId` in today's response resolves — consumers
   * should still keep an unresolvable one reachable instead of dropping it.
   */
  parentId?: string | null;
}

/**
 * Whether a metric may be offered on one surface.
 *
 * A MISSING `supportedUsages` is permissive: a backend that does not declare
 * usages says nothing about this metric, and emptying every picker is a worse
 * failure than the single dead end BDA-6070 closes. A PRESENT list is taken
 * literally, `[]` included — that is the backend saying the metric works on no
 * surface at all.
 */
export function clinicOperatingNumberSupportsUsage(
  descriptor: ClinicOperatingNumberDescriptor,
  usage: ClinicOperatingNumberUsage,
): boolean {
  const usages = descriptor.supportedUsages;
  if (!usages) return true;
  return usages.includes(usage);
}

/** UI-facing operating-number option (label resolved to a single language). */
export interface ClinicOperatingNumberOption {
  id: string;
  label: string;
}

/** Where a LIST condition sources its selectable options. */
export type ClinicOptionsSource =
  | { kind: 'catalog'; families: string[] }
  | { kind: 'fab' }
  | { kind: 'operatingNumber' }
  | { kind: 'enum'; options: ClinicCatalogOption[] };

const DEFAULT_TARGETS: readonly ClinicConditionTarget[] = ['CLINIC'];

export function blueprintDisplayName(
  blueprint: ClinicConditionBlueprint,
  lang = 'de',
): string {
  return (
    blueprint.displayName?.[lang] ??
    blueprint.displayName?.['en'] ??
    blueprint.id
  );
}

export function findFragment(
  blueprint: ClinicConditionBlueprint,
  kind: string,
): ClinicConditionFragment | undefined {
  return blueprint.additionalInformation?.find((f) => f.kind === kind);
}

export function findFragments(
  blueprint: ClinicConditionBlueprint,
  kind: string,
): ClinicConditionFragment[] {
  return (blueprint.additionalInformation ?? []).filter((f) => f.kind === kind);
}

/**
 * The targets/grains a condition applies to. A blueprint with NO
 * `supportedTargets` fragment is CLINIC-only (the implicit default).
 */
export function blueprintTargets(
  blueprint: ClinicConditionBlueprint,
): ClinicConditionTarget[] {
  const fragment = findFragment(blueprint, 'supportedTargets');
  const raw = fragment?.['supportedTargets'];
  if (Array.isArray(raw) && raw.length > 0) {
    // Derived from CLINIC_CONDITION_TARGETS rather than an inline list of
    // literals: the previous hardcoded `t === 'CLINIC' || …` silently dropped
    // any grain added to the type later, so widening the union looked like it
    // worked while the runtime kept filtering the new value out.
    return raw.filter((t): t is ClinicConditionTarget =>
      (CLINIC_CONDITION_TARGETS as readonly unknown[]).includes(t),
    );
  }
  return [...DEFAULT_TARGETS];
}

export function blueprintSupportsTarget(
  blueprint: ClinicConditionBlueprint,
  target: ClinicConditionTarget,
): boolean {
  return blueprintTargets(blueprint).includes(target);
}

/** Catalog families referenced by a LIST (may be several → merge). */
export function blueprintCatalogFamilies(
  blueprint: ClinicConditionBlueprint,
): string[] {
  return findFragments(blueprint, 'catalogReference')
    .map((f) => f['catalogFamily'])
    .filter((v): v is string => typeof v === 'string');
}

function pickLabel(raw: unknown): string | undefined {
  return typeof raw === 'string' && raw.length > 0 ? raw : undefined;
}

/** Map one `labeledOptions` entry (`{ value, label: { de, en } }`) to an option. */
function toLabeledOption(entry: unknown): ClinicCatalogOption | undefined {
  if (typeof entry !== 'object' || entry === null) return undefined;
  const record = entry as Record<string, unknown>;
  const rawValue = record['value'];
  if (typeof rawValue !== 'string' && typeof rawValue !== 'number') {
    return undefined;
  }
  const key = String(rawValue);
  const label = record['label'];
  const labelMap =
    typeof label === 'object' && label !== null
      ? (label as Record<string, unknown>)
      : {};
  const resolved =
    pickLabel(labelMap['de']) ?? pickLabel(labelMap['en']) ?? key;
  return { key, label: resolved };
}

/**
 * Options carried inline by an enum LIST via a `labeledOptions` fragment (a
 * closed enum the backend supplies directly, e.g. `emergencyLevel`). Empty when
 * no such fragment is present.
 */
export function blueprintLabeledOptions(
  blueprint: ClinicConditionBlueprint,
): ClinicCatalogOption[] {
  const fragment = findFragment(blueprint, 'labeledOptions');
  const raw = fragment?.['options'];
  if (!Array.isArray(raw)) return [];
  return raw
    .map(toLabeledOption)
    .filter((option): option is ClinicCatalogOption => option !== undefined);
}

/** Resolve where a LIST condition gets its options from. */
export function blueprintOptionsSource(
  blueprint: ClinicConditionBlueprint,
): ClinicOptionsSource {
  const families = blueprintCatalogFamilies(blueprint);
  if (families.length > 0) return { kind: 'catalog', families };
  if (findFragment(blueprint, 'fabKeyReference')) return { kind: 'fab' };
  if (findFragment(blueprint, 'operatingNumberReference')) {
    return { kind: 'operatingNumber' };
  }
  return { kind: 'enum', options: blueprintLabeledOptions(blueprint) };
}

/**
 * Group a merged catalog by the category its keys carry, so a multi-family LIST
 * says which family each option came from (BDA-6251).
 *
 * `medicalServices` merges 13 `V*` families into 396 options of which only 358
 * labels are distinct: every family ends with its own catch-all `(„Sonstiges“)`
 * — 13 of them — and `Spezialsprechstunde` appears 12 times. Those labels are
 * verbatim from the QB-Richtlinie Anhang 2 and cannot be reworded, and the keys
 * behind them (`VA00` … `VZ00`) are distinct and heavily used, so nothing may be
 * merged away either. The duplicates always sit in DIFFERENT families, so the
 * category heading supplies exactly what the flat label omits.
 *
 * Returns `null` when a heading would add nothing and the caller should render
 * the flat list: an option without a category (enum sources carry none, and a
 * partially categorised list must not be split into a headed and an unheaded
 * half), or a single category — one heading over the whole list is pure noise.
 *
 * Groups are ordered by `category.order`; options keep their catalog order
 * inside a group, and groups of equal order keep first-seen order.
 */
export function groupCatalogOptionsByCategory(
  options: ClinicCatalogOption[],
  lang = 'de',
): ClinicCatalogOptionGroup[] | null {
  const groups = new Map<
    string,
    { order: number; group: ClinicCatalogOptionGroup }
  >();
  for (const option of options) {
    const category = option.category;
    if (!category) return null;
    const existing = groups.get(category.id);
    if (existing) {
      existing.group.items.push(option);
      continue;
    }
    groups.set(category.id, {
      order: category.order,
      group: {
        id: category.id,
        label:
          pickLabel(category.label?.[lang]) ??
          pickLabel(category.label?.['en']) ??
          category.id,
        items: [option],
      },
    });
  }
  if (groups.size < 2) return null;
  return [...groups.values()]
    .sort((a, b) => a.order - b.order)
    .map((entry) => entry.group);
}
